import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePlaybackContext } from "../Playback";
import { useVisibleActionContext } from "../VisibleAction";
import { VolumeContext } from "./VolumeContext";
import { jsx as _jsx } from "react/jsx-runtime";
var VolumeProvider = function VolumeProvider(_ref) {
  var children = _ref.children,
    defaultMuted = _ref.defaultMuted,
    defaultVolumeProp = _ref.defaultVolume,
    onVolumeChange = _ref.onVolumeChange;
  var _useVisibleActionCont = useVisibleActionContext(),
    changeCurrentAction = _useVisibleActionCont.changeCurrentAction;
  var _usePlaybackContext = usePlaybackContext(),
    videoRef = _usePlaybackContext.videoRef,
    isReady = _usePlaybackContext.isReady;
  var defaultVolume = useMemo(function () {
    return typeof defaultVolumeProp === 'number' ? defaultVolumeProp : 1;
  }, [defaultVolumeProp]);
  var _useState = useState({
      muted: !!defaultMuted,
      volume: defaultVolume
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$ = _useState2[0],
    muted = _useState2$.muted,
    volume = _useState2$.volume,
    setState = _useState2[1];

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (isReady && videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = muted;
      if (onVolumeChange) {
        onVolumeChange(volume, muted);
      }
    }
    // Ingoring onVolumeChange to avoid possible infinite changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, isReady, volume, muted]);
  var toggleMute = useCallback(function () {
    var newMutedValue = !muted;
    if (newMutedValue) {
      changeCurrentAction('mute');
    }
    setState({
      muted: newMutedValue,
      // If the action is to unmute and the current volume is 0, return the volume to the initial default value
      volume: !newMutedValue && volume === 0 ? defaultVolume : volume
    });
  }, [muted, volume, defaultVolume, changeCurrentAction]);
  var changeVolume = useCallback(function (newVolume) {
    setState({
      volume: newVolume,
      muted: newVolume === 0
    });
  }, []);
  var volumeUp = useCallback(function () {
    if (volume < 1) {
      var newVolume = volume + 0.1;
      changeVolume(newVolume > 1 ? 1 : newVolume);
      changeCurrentAction('volume');
    }
  }, [volume, changeVolume, changeCurrentAction]);
  var volumeDown = useCallback(function () {
    if (volume > 0) {
      var newVolume = volume - 0.1;
      changeVolume(newVolume < 0 ? 0 : newVolume);
      changeCurrentAction('volume');
    }
  }, [volume, changeVolume, changeCurrentAction]);
  var contextValue = useMemo(function () {
    return {
      muted: muted,
      volume: volume,
      toggleMute: toggleMute,
      changeVolume: changeVolume,
      volumeUp: volumeUp,
      volumeDown: volumeDown
    };
  }, [muted, volume, toggleMute, changeVolume, volumeUp, volumeDown]);
  return /*#__PURE__*/_jsx(VolumeContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default VolumeProvider;